"use client";

import { useCallback, useMemo, useRef } from "react";

import clsx from "clsx";
import { useTranslations } from "next-intl";
import { useMove } from "react-aria";

import { Orb, type OrbTextureName } from "@ui/components/core/Orb/Orb";
import { PlayPauseButton } from "@ui/components/core/PlayPauseButton";
import IconPlayFill from "@ui/central-icons/icons/IconPlayFill";

import type { VoiceWithPreview } from "@ui/components/demos/VoicesWithTTS/types";
import { useHasFinePointer } from "@/app/_utilities/useHasFinePointer";
import { useNativeAudioOrbAnalyser } from "@ui/hooks/useNativeAudioOrbAnalyser";
import { sizes } from "@ui/lib/sizes";
import type { SectionLoadStrategy } from "@/app/_blocks/blocksMeta";

const ORB_TEXTURES: OrbTextureName[] = [
  "creative-3",
  "creative-9",
  "creative-5",
  "creative-1",
  "creative-4",
  "creative-8",
];
const DRAG_THRESHOLD = 6;
const SWIPE_THRESHOLD = 24;

const STAGE_SLOTS = {
  hiddenLeft: { offsetRem: -47.5, sizeRem: 7.5, topRem: 4.5, opacity: 0, zIndex: 0 },
  farLeft: { offsetRem: -36.75, sizeRem: 10, topRem: 3.75, opacity: 0.4, zIndex: 1 },
  left: { offsetRem: -21.375, sizeRem: 13.875, topRem: 1.875, opacity: 0.6, zIndex: 2 },
  center: { offsetRem: 0, sizeRem: 17.625, topRem: 0, opacity: 1, zIndex: 3 },
  right: { offsetRem: 21.375, sizeRem: 13.875, topRem: 1.875, opacity: 0.6, zIndex: 2 },
  farRight: { offsetRem: 36.75, sizeRem: 10, topRem: 3.75, opacity: 0.4, zIndex: 1 },
  hiddenRight: { offsetRem: 47.5, sizeRem: 7.5, topRem: 4.5, opacity: 0, zIndex: 0 },
} as const;

type SlotName = keyof typeof STAGE_SLOTS;
type SlotStyle = {
  offsetRem: number;
  sizeRem: number;
  topRem: number;
  opacity: number;
  zIndex: number;
};

const SLOT_SEQUENCE: readonly { name: SlotName; relativeIndex: number }[] = [
  { name: "hiddenLeft", relativeIndex: -3 },
  { name: "farLeft", relativeIndex: -2 },
  { name: "left", relativeIndex: -1 },
  { name: "center", relativeIndex: 0 },
  { name: "right", relativeIndex: 1 },
  { name: "farRight", relativeIndex: 2 },
  { name: "hiddenRight", relativeIndex: 3 },
];

const MOBILE_STAGE_SLOTS = {
  hiddenLeft: { offsetRem: -18.25, sizeRem: 8.75, topRem: 1.875, opacity: 0, zIndex: 0 },
  left: { offsetRem: -12.5, sizeRem: 8.75, topRem: 1.875, opacity: 0.4, zIndex: 1 },
  center: { offsetRem: 0, sizeRem: 12.5, topRem: 0, opacity: 1, zIndex: 3 },
  right: { offsetRem: 12.5, sizeRem: 8.75, topRem: 1.875, opacity: 0.4, zIndex: 1 },
  hiddenRight: { offsetRem: 18.25, sizeRem: 8.75, topRem: 1.875, opacity: 0, zIndex: 0 },
} as const;

type MobileSlotName = keyof typeof MOBILE_STAGE_SLOTS;
type OrbSlotName = SlotName | MobileSlotName;

const MOBILE_SLOT_SEQUENCE: readonly { name: MobileSlotName; relativeIndex: number }[] = [
  { name: "hiddenLeft", relativeIndex: -2 },
  { name: "left", relativeIndex: -1 },
  { name: "center", relativeIndex: 0 },
  { name: "right", relativeIndex: 1 },
  { name: "hiddenRight", relativeIndex: 2 },
];

function getOrbTexture(index: number) {
  return ORB_TEXTURES[index % ORB_TEXTURES.length];
}

function getStageItems(
  voices: VoiceWithPreview[],
  activeIndex: number,
  slots: Record<SlotName, SlotStyle> = STAGE_SLOTS,
) {
  return SLOT_SEQUENCE.flatMap(slot => {
    const index = activeIndex + slot.relativeIndex;
    const voice = voices[index];
    if (!voice) return [];

    return [
      {
        slotName: slot.name,
        slotConfig: slots[slot.name],
        voice,
        voiceIndex: index,
      },
    ];
  });
}

function getMobileStageItems(
  voices: VoiceWithPreview[],
  activeIndex: number,
  slots: Record<MobileSlotName, SlotStyle> = MOBILE_STAGE_SLOTS,
) {
  return MOBILE_SLOT_SEQUENCE.flatMap(slot => {
    const index = activeIndex + slot.relativeIndex;
    const voice = voices[index];
    if (!voice) return [];

    return [
      {
        slotName: slot.name,
        slotConfig: slots[slot.name],
        voice,
        voiceIndex: index,
      },
    ];
  });
}

function OrbPreviewButton({
  voice,
  slotName,
  slotStyle,
  baseSizeRem,
  voiceIndex,
  isPlaying,
  outputAnalyser,
  onSelectOrbIndex,
  onToggleActiveVoice,
  ensureAudioContext,
  shouldSuppressClick,
  resetDragDistance,
  preload = false,
}: {
  voice: VoiceWithPreview;
  slotName: OrbSlotName;
  slotStyle: SlotStyle;
  baseSizeRem: number;
  voiceIndex: number;
  isPlaying: boolean;
  outputAnalyser: AnalyserNode | null;
  onSelectOrbIndex: (targetIndex: number) => void;
  onToggleActiveVoice: () => void;
  ensureAudioContext: () => void;
  shouldSuppressClick: () => boolean;
  resetDragDistance: () => void;
  preload?: boolean;
}) {
  const tVoicesWithTTS = useTranslations("ClientKeys.VoicesWithTTS");
  const isCenter = slotName === "center";
  const isSideClickable = slotName === "left" || slotName === "right";
  const isHoverable = isCenter || isSideClickable;
  const isInteractive = isCenter || isSideClickable;

  const actionLabel = isPlaying
    ? tVoicesWithTTS("pause_preview", { name: voice.name })
    : tVoicesWithTTS("play_preview", { name: voice.name });

  return (
    <div
      className={clsx("tw-group tw-absolute tw-transition", isSideClickable && "hover:!tw-opacity-100")}
      style={{
        left: "50%",
        width: `${baseSizeRem}rem`,
        height: `${baseSizeRem}rem`,
        transformOrigin: "top center",
        transform: `translateX(calc(-50% + ${slotStyle.offsetRem}rem)) translateY(${slotStyle.topRem}rem) scale(${slotStyle.sizeRem / baseSizeRem})`,
        opacity: slotStyle.opacity,
        zIndex: slotStyle.zIndex,
        transitionDuration: "360ms",
        transitionTimingFunction: "ease",
      }}
    >
      <div className='tw-relative tw-size-full'>
        <button
          type='button'
          aria-label={actionLabel}
          className={clsx(
            "tw-absolute tw-inset-0 tw-z-10 tw-rounded-full tw-outline-none focus-visible:tw-outline-focus",
            isInteractive && "tw-cursor-pointer",
          )}
          disabled={!isInteractive}
          onClick={() => {
            if (shouldSuppressClick()) {
              resetDragDistance();
              return;
            }

            if (isCenter) {
              ensureAudioContext();
              onToggleActiveVoice();
              return;
            }

            ensureAudioContext();

            if (slotName === "left") {
              onSelectOrbIndex(voiceIndex);
              return;
            }

            if (slotName === "right") {
              onSelectOrbIndex(voiceIndex);
              return;
            }
          }}
        />

        {isHoverable && (
          <div
            aria-hidden='true'
            className='tw-pointer-events-none tw-absolute -tw-inset-1 tw-rounded-full tw-border-4 tw-border-gray-200 tw-opacity-0 tw-scale-[0.985] tw-transition tw-duration-200 group-hover:tw-opacity-100 group-hover:tw-scale-100'
          />
        )}

        <div className='tw-relative tw-size-full tw-overflow-hidden tw-rounded-full tw-ring-0.5 tw-ring-inset tw-ring-black/10'>
          <Orb
            texture={getOrbTexture(voiceIndex)}
            animated={isCenter}
            outputAnalyser={isCenter ? outputAnalyser : null}
            placeholderBlur={4}
            image={{ sizes: sizes("14rem", { md: "18rem" }) }}
            preload={preload}
          />
        </div>

        <div
          className={clsx(
            "tw-pointer-events-none tw-absolute tw-inset-0 tw-flex tw-items-center tw-justify-center tw-transition-opacity tw-duration-300",
            isCenter ? "tw-opacity-100" : "tw-opacity-0",
          )}
        >
          <PlayPauseButton isPlaying={isPlaying} variant='elevated' className='tw-size-14 tw-bg-white tw-text-black' />
        </div>

        {isSideClickable && (
          <div className='tw-pointer-events-none tw-absolute tw-inset-0 tw-flex tw-items-center tw-justify-center'>
            <div
              aria-hidden='true'
              className='tw-flex tw-size-14 tw-items-center tw-justify-center tw-rounded-full tw-bg-white tw-text-black tw-opacity-0 tw-scale-95 tw-transition tw-duration-200 group-hover:tw-opacity-100 group-hover:tw-scale-100'
              style={{
                boxShadow:
                  "0 0 0 1px rgba(0, 0, 0, 0.06), 0 1px 2px 0 rgba(0, 0, 0, 0.04), 0 2px 4px 0 rgba(0, 0, 0, 0.04)",
              }}
            >
              <IconPlayFill className='tw-size-5' />
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export interface OrbCarouselContentProps {
  voices: VoiceWithPreview[];
  activeIndex: number;
  playingId: string | null;
  isPaused: boolean;
  onSelectOrbIndex: (targetIndex: number) => void;
  onToggleActiveVoice: () => void;
  onPrevious: () => void;
  onNext: () => void;
  desktopSlots?: Record<SlotName, SlotStyle>;
  mobileSlots?: Record<MobileSlotName, SlotStyle>;
  desktopStageHeight?: string;
  mobileStageHeight?: string;
  centered?: boolean;
  loadStrategy?: SectionLoadStrategy;
}

export function OrbCarouselContent({
  voices,
  activeIndex,
  playingId,
  isPaused,
  onSelectOrbIndex,
  onToggleActiveVoice,
  onPrevious,
  onNext,
  desktopSlots = STAGE_SLOTS,
  mobileSlots = MOBILE_STAGE_SLOTS,
  desktopStageHeight = "22rem",
  mobileStageHeight,
  centered = false,
  loadStrategy,
}: OrbCarouselContentProps) {
  const hasFinePointer = useHasFinePointer();
  const { ensureAudioContext, outputAnalyser } = useNativeAudioOrbAnalyser("OrbCarouselContent");
  const moveDistanceRef = useRef(0);
  const swipeDistanceRef = useRef(0);

  const activeVoice = voices[activeIndex];
  const desktopStageItems = useMemo(
    () => getStageItems(voices, activeIndex, desktopSlots),
    [voices, activeIndex, desktopSlots],
  );
  const mobileStageItems = useMemo(
    () => getMobileStageItems(voices, activeIndex, mobileSlots),
    [voices, activeIndex, mobileSlots],
  );
  const canNavigate = voices.length > 1;
  const resetDragDistance = useCallback(() => {
    moveDistanceRef.current = 0;
  }, []);
  const resetSwipeDistance = useCallback(() => {
    swipeDistanceRef.current = 0;
  }, []);

  const handleMoveEnd = useCallback(() => {
    const swipeDistance = swipeDistanceRef.current;
    resetSwipeDistance();

    if (Math.abs(swipeDistance) < SWIPE_THRESHOLD || !canNavigate) {
      resetDragDistance();
      return;
    }

    if (swipeDistance > 0) {
      onPrevious();
    } else {
      onNext();
    }

    resetDragDistance();
  }, [canNavigate, onNext, onPrevious, resetDragDistance, resetSwipeDistance]);

  const { moveProps } = useMove({
    onMoveStart: () => {
      resetDragDistance();
      resetSwipeDistance();
    },
    onMoveEnd: handleMoveEnd,
    onMove: e => {
      if (e.pointerType === "keyboard") {
        if (e.deltaX > 0) onNext();
        else if (e.deltaX < 0) onPrevious();
        return;
      }

      if (hasFinePointer) return;

      moveDistanceRef.current += Math.abs(e.deltaX);
      swipeDistanceRef.current += e.deltaX;
    },
  });

  if (!activeVoice) return null;

  return (
    <div className={clsx("tw-flex tw-size-full tw-flex-1", centered ? "tw-items-center" : "tw-items-start tw-pt-4")}>
      <div
        className={clsx("tw-relative tw-w-full tw-overflow-hidden tw-pt-1")}
        style={{ touchAction: "pan-y" }}
        {...moveProps}
      >
        <div
          className='tw-relative tw-w-full md:tw-hidden'
          style={{ height: mobileStageHeight ?? "15rem", maxHeight: "100%" }}
        >
          {mobileStageItems.map(item => (
            <OrbPreviewButton
              key={item.voice.voiceId}
              voice={item.voice}
              slotName={item.slotName}
              slotStyle={item.slotConfig}
              baseSizeRem={mobileSlots.center.sizeRem}
              voiceIndex={item.voiceIndex}
              isPlaying={playingId === item.voice.voiceId && !isPaused}
              outputAnalyser={outputAnalyser}
              onSelectOrbIndex={onSelectOrbIndex}
              onToggleActiveVoice={onToggleActiveVoice}
              ensureAudioContext={ensureAudioContext}
              shouldSuppressClick={() => moveDistanceRef.current > DRAG_THRESHOLD}
              resetDragDistance={resetDragDistance}
              preload={loadStrategy === "lcpOptimization" && item.slotName === "center"}
            />
          ))}
        </div>
        <div
          className='tw-relative tw-hidden tw-w-full md:tw-block'
          style={{ height: desktopStageHeight, maxHeight: "100%" }}
        >
          {desktopStageItems.map(item => (
            <OrbPreviewButton
              key={item.voice.voiceId}
              voice={item.voice}
              slotName={item.slotName}
              slotStyle={item.slotConfig}
              baseSizeRem={desktopSlots.center.sizeRem}
              voiceIndex={item.voiceIndex}
              isPlaying={playingId === item.voice.voiceId && !isPaused}
              outputAnalyser={outputAnalyser}
              onSelectOrbIndex={onSelectOrbIndex}
              onToggleActiveVoice={onToggleActiveVoice}
              ensureAudioContext={ensureAudioContext}
              shouldSuppressClick={() => moveDistanceRef.current > DRAG_THRESHOLD}
              resetDragDistance={resetDragDistance}
              preload={loadStrategy === "lcpOptimization" && item.slotName === "center"}
            />
          ))}
        </div>
      </div>
    </div>
  );
}
